import { Platform } from "react-native"
import Constants from "expo-constants"
import { analyticsWrap } from "src/tags/analytics"
import { AccountModel } from "src/types/user/AccountModel"
import { MyOrganization } from "src/queries/getMyOrganizations"

const QUESTION_IS_MARRIED = 1
const QUESTION_HAS_CHILD = 2

// Firebase Analytics用のユーザープロパティ型定義
export type FirebaseUserProperties = {
  appVersion: string
  model: string
  platform: string
  channelType?: string | null
  optIn?: string | null
  isLogin?: string | null
  accountId?: string | null
  domain?: string | null
  isManager?: string | null
  gender?: string | null
  job?: string | null
  school?: string | null
  prefecture?: string | null
  age?: string | null
  married?: string | null
  child?: string | null
  joinedFreeOrganization?: string | null
}

// 送信フラグ管理（アプリ起動ごと、ログインごとにリセット）
let hasSetInitialProperties = false

/**
 * 年齢を計算する関数
 */
const getAge = (birthDate?: string | Date | null): number => {
  if (!birthDate) return 0

  const today = new Date()
  const birth = typeof birthDate === "string" ? new Date(birthDate) : birthDate

  const birthYear = birth.getFullYear()
  const birthMonth = birth.getMonth()
  const birthDay = birth.getDate()

  let age = today.getFullYear() - birthYear

  if (today.getMonth() < birthMonth || (today.getMonth() === birthMonth && today.getDate() < birthDay)) {
    age--
  }
  return age
}

/**
 * デバイスチャンネルを取得
 */
const getDeviceChannel = (): string | null => {
  if (Platform.OS === "web") return null
  if (Platform.OS === "ios") return "APNS"
  return "GCM"
}

/**
 * Firebase Analyticsにユーザープロパティを送信する汎用関数
 * @param account - アカウント情報
 * @param myOrganizations - 所属団体情報
 * @param optIn - プッシュ通知の許可状態
 * @param deviceToken - デバイストークン
 * @param forceUpdate - 強制更新フラグ（ログイン時等に使用）
 */
export async function sendFirebasePropertiesIfNeeded(
  account?: AccountModel | null,
  myOrganizations?: MyOrganization[] | null,
  optIn?: boolean,
  deviceToken?: string | null,
  forceUpdate = false
) {
  // 強制更新でない場合は初回フラグをチェック
  if (!forceUpdate && hasSetInitialProperties) {
    console.log("[Firebase Analytics] Already sent initial properties, skipping...")
    return
  }

  // accountが必須（myOrganizationsは空配列でもOK）
  if (!account) {
    console.log("[Firebase Analytics] Missing account data, skipping...")
    return
  }

  // Web環境またはデバイストークンがない場合はスキップ（初回起動時を除く）
  if (!forceUpdate && (Platform.OS === "web" || !deviceToken)) {
    console.log("[Firebase Analytics] Web platform or no device token, skipping...")
    return
  }

  try {
    // プロパティを構築
    const questionAnswers = account.questionAnswers ?? {}
    const email = account.email
    const isManager = myOrganizations?.some((o) => o.role === "manager") ?? false
    const joinedFreeOrganization = myOrganizations?.some((o) => !o.paidFunctionEnabled || o.showAdsFlg) ?? false

    const properties: FirebaseUserProperties = {
      accountId: account.id ?? "",
      channelType: getDeviceChannel(),
      optIn: optIn !== undefined ? String(optIn) : undefined,
      appVersion: Constants.expoConfig?.version ?? "",
      model: Constants.deviceName ?? "",
      platform: Platform.OS,
      isManager: isManager ? "true" : "false",
      isLogin: "true",
      domain: email?.substring(email?.lastIndexOf("@") + 1) ?? "",
      gender: account.gender ?? "",
      job: account.jobId ?? "",
      school: account.schoolId ?? "",
      prefecture: account.prefectureId ?? "",
      age: account.birthDate ? getAge(account.birthDate).toString() : "",
      married: questionAnswers[QUESTION_IS_MARRIED] ?? "",
      child: questionAnswers[QUESTION_HAS_CHILD] ?? "",
      joinedFreeOrganization: joinedFreeOrganization ? "true" : "false",
    }

    // Firebase Analyticsに送信
    console.log("[Firebase Analytics] Sending user properties:", properties)
    analyticsWrap.setUserProperties(properties)

    // 強制更新でない場合は初回フラグを立てる
    if (!forceUpdate) {
      hasSetInitialProperties = true
      console.log("[Firebase Analytics] Initial properties sent successfully")
    }
  } catch (error) {
    console.error("[Firebase Analytics] Error sending properties:", error)
  }
}

/**
 * 特定のプロパティのみを更新する関数
 * @param key - プロパティのキー
 * @param value - プロパティの値
 */
export function updateFirebaseProperty(key: string, value: string) {
  try {
    console.log(`[Firebase Analytics] Updating property: ${key} = ${value}`)
    analyticsWrap.setUserProperties({ [key]: value })
  } catch (error) {
    console.error(`[Firebase Analytics] Error updating property ${key}:`, error)
  }
}

/**
 * 複数のプロパティを一度に更新する関数
 * @param properties - 更新するプロパティのオブジェクト
 */
export function updateFirebaseProperties(properties: Partial<FirebaseUserProperties>) {
  try {
    console.log("[Firebase Analytics] Updating properties:", properties)
    analyticsWrap.setUserProperties(properties)
  } catch (error) {
    console.error("[Firebase Analytics] Error updating properties:", error)
  }
}

/**
 * ログアウト時にFirebase Analyticsのプロパティをクリアする関数
 */
export function clearFirebasePropertiesForLogout() {
  try {
    console.log("[Firebase Analytics] Clearing properties for logout")
    updateFirebaseProperties({
      isLogin: "false",
      accountId: "",
      isManager: "",
      joinedFreeOrganization: "",
    })
    // フラグをリセット（次回ログイン時に送信するため）
    hasSetInitialProperties = false
  } catch (error) {
    console.error("[Firebase Analytics] Error clearing properties for logout:", error)
  }
}
